import { useMemo, useState } from "react";
import clsx from "clsx";

function reorderItems<T>(items: T[], from: number, to: number): T[] {
  if (from === to || from < 0 || from >= items.length) return items;
  const next = [...items];
  const [moved] = next.splice(from, 1);
  if (!moved) return items;
  let insertAt = to;
  if (from < to) insertAt -= 1;
  insertAt = Math.max(0, Math.min(insertAt, next.length));
  next.splice(insertAt, 0, moved);
  return next;
}

export interface SortableColumnPickerProps {
  available: string[];
  selected: string[];
  requiredColumns?: readonly string[];
  getLabel: (key: string) => string;
  onSelectedChange: (next: string[]) => void;
  disabled?: boolean;
}

export function SortableColumnPicker({
  available,
  selected,
  requiredColumns = [],
  getLabel,
  onSelectedChange,
  disabled = false,
}: SortableColumnPickerProps) {
  const requiredSet = useMemo(() => new Set(requiredColumns), [requiredColumns]);
  const unselected = useMemo(
    () => available.filter((col) => !selected.includes(col)),
    [available, selected],
  );

  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dropIndex, setDropIndex] = useState<number | null>(null);

  function toggle(col: string, checked: boolean) {
    if (disabled) return;
    if (requiredSet.has(col)) return;
    if (checked) {
      if (!selected.includes(col)) onSelectedChange([...selected, col]);
      return;
    }
    onSelectedChange(selected.filter((c) => c !== col));
  }

  function moveItem(from: number, to: number) {
    if (disabled) return;
    onSelectedChange(reorderItems(selected, from, to));
  }

  function resolveDropIndex(index: number, before: boolean): number {
    return before ? index : index + 1;
  }

  return (
    <div className="space-y-4">
      <section>
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-label-secondary">
          Angezeigte Spalten
        </h3>
        <p className="mb-2 text-xs text-label-tertiary">
          Reihenfolge per Ziehen am Griff ändern.
        </p>
        {selected.length === 0 ? (
          <p className="rounded-md bg-fill-tertiary px-3 py-2 text-sm text-label-secondary">
            Keine Spalten ausgewählt.
          </p>
        ) : (
          <ul className="space-y-1">
            {selected.map((col, idx) => {
              const isRequired = requiredSet.has(col);
              const isDragging = dragIndex === idx;
              const showDropBefore = dropIndex === idx;
              return (
                <li key={col}>
                  {showDropBefore ? (
                    <div className="mb-1 h-0.5 rounded-full bg-accent" aria-hidden />
                  ) : null}
                  <div
                    className={clsx(
                      "flex items-center gap-2 rounded-md border border-transparent px-2 py-1.5 text-sm transition",
                      isDragging && "opacity-50",
                      !disabled && "hover:bg-fill-tertiary",
                    )}
                    onDragOver={(e) => {
                      if (disabled || dragIndex === null) return;
                      e.preventDefault();
                      const rect = e.currentTarget.getBoundingClientRect();
                      const before = e.clientY - rect.top < rect.height / 2;
                      setDropIndex(resolveDropIndex(idx, before));
                    }}
                    onDragLeave={() => {
                      if (dropIndex === idx || dropIndex === idx + 1) setDropIndex(null);
                    }}
                    onDrop={(e) => {
                      if (disabled || dragIndex === null) return;
                      e.preventDefault();
                      const rect = e.currentTarget.getBoundingClientRect();
                      const before = e.clientY - rect.top < rect.height / 2;
                      const to = resolveDropIndex(idx, before);
                      moveItem(dragIndex, to);
                      setDragIndex(null);
                      setDropIndex(null);
                    }}
                  >
                    <button
                      type="button"
                      className={clsx(
                        "shrink-0 cursor-grab px-1 text-label-tertiary active:cursor-grabbing",
                        disabled && "cursor-not-allowed opacity-40",
                      )}
                      title="Ziehen zum Sortieren"
                      aria-label={`${getLabel(col)} verschieben`}
                      draggable={!disabled}
                      disabled={disabled}
                      onDragStart={(e) => {
                        if (disabled) return;
                        setDragIndex(idx);
                        e.dataTransfer.effectAllowed = "move";
                        e.dataTransfer.setData("text/plain", col);
                      }}
                      onDragEnd={() => {
                        setDragIndex(null);
                        setDropIndex(null);
                      }}
                    >
                      ⠿
                    </button>
                    <label className="flex min-w-0 flex-1 cursor-pointer items-center gap-2">
                      <input
                        type="checkbox"
                        className="h-4 w-4 shrink-0 rounded border-separator-opaque text-accent focus:ring-accent-soft"
                        checked
                        disabled={disabled || isRequired}
                        onChange={() => toggle(col, false)}
                      />
                      <span className="truncate text-label">{getLabel(col)}</span>
                    </label>
                    {isRequired ? (
                      <span className="shrink-0 text-[10px] uppercase tracking-wide text-label-tertiary">
                        Pflicht
                      </span>
                    ) : null}
                  </div>
                </li>
              );
            })}
            <li
              className={clsx(
                "min-h-2 rounded-md transition",
                dropIndex === selected.length && "bg-accent/10",
              )}
              onDragOver={(e) => {
                if (disabled || dragIndex === null) return;
                e.preventDefault();
                setDropIndex(selected.length);
              }}
              onDragLeave={() => {
                if (dropIndex === selected.length) setDropIndex(null);
              }}
              onDrop={(e) => {
                if (disabled || dragIndex === null) return;
                e.preventDefault();
                moveItem(dragIndex, selected.length);
                setDragIndex(null);
                setDropIndex(null);
              }}
            >
              {dropIndex === selected.length ? (
                <div className="my-1 h-0.5 rounded-full bg-accent" aria-hidden />
              ) : null}
            </li>
          </ul>
        )}
      </section>

      {unselected.length > 0 ? (
        <section>
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-label-secondary">
            Weitere Spalten
          </h3>
          <ul className="grid grid-cols-1 gap-1 sm:grid-cols-2">
            {unselected.map((col) => (
              <li key={col}>
                <label
                  className={clsx(
                    "flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm",
                    disabled ? "opacity-50" : "hover:bg-fill-tertiary",
                  )}
                >
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-separator-opaque text-accent focus:ring-accent-soft"
                    checked={false}
                    disabled={disabled}
                    onChange={() => toggle(col, true)}
                  />
                  <span className="flex-1 text-label">{getLabel(col)}</span>
                </label>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}
